import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Alert,
  Paper,
  Snackbar,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';

// Suggestion prompts for initial email generation
const SUGGESTION_PROMPTS = [
  'Focus on their workflow usage',
  'Mention upcoming training session',
  'Keep it brief',
  'Emphasize time saved',
  'Encourage team adoption',
];

// Suggestion prompts for editing the email
const EDIT_SUGGESTIONS = [
  'Make it shorter',
  'More enthusiastic',
  'Add call-to-action',
  'Simpler language',
  'More formal tone',
];
import { generateCustomerEmail, editCustomerEmail } from '../api/dashboard';
import { copyHtmlToClipboard, copyTextToClipboard } from '../utils/clipboard';
import type { FilterOption, GenerateEmailResponse } from '../types';
import { formatOrgDisplayName } from '../utils/display';

interface GenerateEmailModalProps {
  open: boolean;
  onClose: () => void;
  availableOrgs: FilterOption[];
  dateRange: {
    start: Date;
    end: Date;
  };
  preselectedOrgId?: string;
}

export default function GenerateEmailModal({
  open,
  onClose,
  availableOrgs,
  dateRange,
  preselectedOrgId,
}: GenerateEmailModalProps) {
  const preselectedOrg = preselectedOrgId
    ? availableOrgs.find((o) => o.id === preselectedOrgId) || null
    : null;

  const [selectedOrg, setSelectedOrg] = useState<FilterOption | null>(preselectedOrg);
  const [customInstructions, setCustomInstructions] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [emailData, setEmailData] = useState<GenerateEmailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [copiedSubject, setCopiedSubject] = useState(false);
  const [editInstructions, setEditInstructions] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const editSectionRef = useRef<HTMLDivElement>(null);

  // Scroll to edit section when email is generated
  useEffect(() => {
    if (emailData && editSectionRef.current) {
      setTimeout(() => {
        editSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [emailData]);

  const handleGenerate = useCallback(async () => {
    if (!selectedOrg) return;

    setIsGenerating(true);
    setError(null);
    setEmailData(null);

    try {
      const data = await generateCustomerEmail(
        selectedOrg.id,
        dateRange.start,
        dateRange.end,
        customInstructions || undefined
      );
      setEmailData(data);
    } catch (err) {
      console.error('Failed to generate email:', err);
      setError('Failed to generate email. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [selectedOrg, dateRange, customInstructions]);

  const handleCopyHtml = useCallback(async () => {
    if (!emailData) return;

    const success = await copyHtmlToClipboard(emailData.htmlContent);
    if (success) {
      setCopiedHtml(true);
      setSnackbar({ open: true, message: 'Email copied with formatting!' });
      setTimeout(() => setCopiedHtml(false), 2000);
    } else {
      setSnackbar({ open: true, message: 'Failed to copy. Try the plain text option.' });
    }
  }, [emailData]);

  const handleCopyPlainText = useCallback(async () => {
    if (!emailData) return;

    const success = await copyTextToClipboard(emailData.plainText);
    if (success) {
      setCopiedText(true);
      setSnackbar({ open: true, message: 'Plain text copied!' });
      setTimeout(() => setCopiedText(false), 2000);
    } else {
      setSnackbar({ open: true, message: 'Failed to copy text.' });
    }
  }, [emailData]);

  const handleCopySubject = useCallback(async () => {
    if (!emailData) return;

    const success = await copyTextToClipboard(emailData.subjectLine);
    if (success) {
      setCopiedSubject(true);
      setSnackbar({ open: true, message: 'Subject line copied!' });
      setTimeout(() => setCopiedSubject(false), 2000);
    } else {
      setSnackbar({ open: true, message: 'Failed to copy subject.' });
    }
  }, [emailData]);

  const handleEdit = useCallback(async () => {
    if (!emailData || !editInstructions.trim()) return;

    setIsEditing(true);
    setError(null);

    try {
      const data = await editCustomerEmail(emailData.sessionId, editInstructions);
      // Update emailData with the edited content while preserving metricsSummary
      setEmailData({
        ...emailData,
        htmlContent: data.htmlContent,
        plainText: data.plainText,
        subjectLine: data.subjectLine,
        sessionId: data.sessionId,
      });
      setEditInstructions('');
      setSnackbar({ open: true, message: 'Email updated!' });
    } catch (err) {
      console.error('Failed to edit email:', err);
      setError('Failed to edit email. Please try again.');
    } finally {
      setIsEditing(false);
    }
  }, [emailData, editInstructions]);

  const handleClose = useCallback(() => {
    setSelectedOrg(preselectedOrg);
    setCustomInstructions('');
    setEmailData(null);
    setError(null);
    setEditInstructions('');
    onClose();
  }, [onClose, preselectedOrg]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '95vh',
            bgcolor: '#0f172a',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          Generate Customer Success Email
          <Chip
            label="AI Powered"
            size="small"
            sx={{
              ml: 1,
              bgcolor: 'rgba(139, 92, 246, 0.2)',
              color: '#a78bfa',
              fontSize: '0.7rem',
            }}
          />
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate a personalized customer success email based on usage metrics and actual
              queries/workflows. The email will highlight achievements and encourage further adoption.
            </Typography>

            <Autocomplete
              size="small"
              options={availableOrgs}
              getOptionLabel={(option: FilterOption) => formatOrgDisplayName(option.name)}
              value={selectedOrg}
              onChange={(_, newValue) => {
                setSelectedOrg(newValue);
                setEmailData(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Organization"
                  placeholder="Choose an organization..."
                />
              )}
              sx={{ minWidth: 300, mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              label="Custom Instructions (Optional)"
              placeholder="e.g., 'Focus on their workflow usage' or 'Mention the upcoming training session'"
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
            />

            {/* Suggestion chips */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {SUGGESTION_PROMPTS.map((suggestion) => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  size="small"
                  onClick={() => setCustomInstructions(suggestion)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: 'rgba(139, 92, 246, 0.1)',
                    '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.2)' },
                  }}
                />
              ))}
            </Box>

            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={!selectedOrg || isGenerating}
              startIcon={
                isGenerating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
              sx={{
                mt: 2,
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                },
              }}
            >
              {isGenerating ? 'Generating...' : 'Generate Email'}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isGenerating && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Analyzing usage data and generating personalized email...
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
                This may take 10-20 seconds
              </Typography>
            </Box>
          )}

          {emailData && !isGenerating && (
            <Box>
              {/* Subject Line */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Suggested Subject Line
                </Typography>
                <Paper
                  sx={{
                    p: 1.5,
                    bgcolor: 'rgba(139, 92, 246, 0.1)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body1" fontWeight={500}>
                    {emailData.subjectLine}
                  </Typography>
                  <Tooltip title="Copy subject">
                    <IconButton size="small" onClick={handleCopySubject}>
                      {copiedSubject ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                    </IconButton>
                  </Tooltip>
                </Paper>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Email Preview */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Email Preview
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={copiedText ? <CheckIcon /> : <TextFieldsIcon />}
                      onClick={handleCopyPlainText}
                      color={copiedText ? 'success' : 'inherit'}
                    >
                      Copy Plain Text
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={copiedHtml ? <CheckIcon /> : <ContentCopyIcon />}
                      onClick={handleCopyHtml}
                      color={copiedHtml ? 'success' : 'primary'}
                    >
                      Copy with Formatting
                    </Button>
                  </Box>
                </Box>

                <Paper
                  sx={{
                    p: 3,
                    bgcolor: '#ffffff',
                    color: '#1a1a1a',
                    maxHeight: 'calc(95vh - 500px)',
                    overflow: 'auto',
                    borderRadius: 2,
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: emailData.htmlContent }} />
                </Paper>
              </Box>

              {/* Metrics Summary */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Data Used in Email
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={`${emailData.metricsSummary.activeUsers} active users`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`${emailData.metricsSummary.totalQueries} queries`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`${emailData.metricsSummary.totalWorkflows} workflows`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`${emailData.metricsSummary.timeSavedHours}h saved`}
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Edit Section */}
              <Divider sx={{ my: 2 }} />
              <Box ref={editSectionRef}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Refine the email
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g., 'make it shorter', 'emphasize time saved', 'add call-to-action'"
                    value={editInstructions}
                    onChange={(e) => setEditInstructions(e.target.value)}
                    disabled={isEditing}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleEdit();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleEdit}
                    disabled={!editInstructions.trim() || isEditing}
                    startIcon={isEditing ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
                    sx={{ minWidth: 100 }}
                  >
                    {isEditing ? 'Editing...' : 'Edit'}
                  </Button>
                </Box>

                {/* Quick edit chips */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {EDIT_SUGGESTIONS.map((suggestion) => (
                    <Chip
                      key={suggestion}
                      label={suggestion}
                      size="small"
                      onClick={() => setEditInstructions(suggestion)}
                      disabled={isEditing}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'rgba(99, 102, 241, 0.1)',
                        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.2)' },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {!selectedOrg && !isGenerating && !emailData && (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <EmailIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
              <Typography>Select an organization and click Generate to create a personalized email</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
